import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CRMApiAdapter } from '../adapters/CRMApiAdapter';
import { ORDER_STATUS_ID } from '../config/statusConfig';
import { CRMModel, CrmStatusModel } from '../models/CRM';

export const useConfirmOrder = (statusList?: CrmStatusModel[]) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: number) => apiAdapter.confirmOrdersBulk([orderId]),
    onMutate: async (orderId: number) => {
      try {
        await queryClient.cancelQueries({ queryKey: ['crm-orders'] });
        await queryClient.cancelQueries({ queryKey: ['crm-orders-count-by-status'] });

        const previousOrdersQueries = queryClient.getQueriesData({ queryKey: ['crm-orders'] });
        const previousCountsQueries = queryClient.getQueriesData({ queryKey: ['crm-orders-count-by-status'] });

        // Buscar el nuevo estado prioritariamente por nombre (flexible)
        const confirmedStatus =
          statusList && Array.isArray(statusList)
            ? statusList.find((s) => {
                const name = (s?.name || '').toLowerCase().trim();
                return name === 'confirmado' || name === 'confirmed' || name.includes('confirmad');
              })
            : undefined;

        const fallbackStatus =
          statusList && Array.isArray(statusList)
            ? statusList.find((s) => s?.id === ORDER_STATUS_ID.CONFIRMED)
            : undefined;
        const targetStatus = confirmedStatus || fallbackStatus || { id: ORDER_STATUS_ID.CONFIRMED, name: 'Confirmado' };
        const targetStatusId = targetStatus.id;

        const newStatusMetadata = {
          ...(targetStatus as object),
          managed_at: new Date().toISOString(),
          managed_by: { id: 0, name: 'Yo', email: '' },
        };

        // 1. Encontrar el pedido y su estado anterior
        let orderToMove: CRMModel | null = null;
        let oldCrmStatusId: number | null = null;

        previousOrdersQueries.forEach(([, data]: any) => {
          if (!orderToMove && data?.pages) {
            data.pages.forEach((page: any) => {
              if (!page || !Array.isArray(page.orders)) return;
              const found = page.orders.find((o: any) => Number(o?.id) === Number(orderId));
              if (found) {
                orderToMove = { ...(found as object) } as CRMModel;
                const status = Array.isArray(found.crm_status) ? found.crm_status[0] : found.crm_status;
                oldCrmStatusId = status?.id ?? null;
              }
            });
          }
        });

        if (orderToMove) {
          // Preparar el pedido actualizado
          const updatedOrder = {
            ...(orderToMove as object),
            crm_status: [newStatusMetadata],
            crm_status_id: targetStatusId,
            status_id: ORDER_STATUS_ID.CONFIRMED,
          };

          // 2. Actualizar TODAS las queries de pedidos
          queryClient.setQueriesData({ queryKey: ['crm-orders'] }, (old: any, query: any) => {
            if (!old || !Array.isArray(old.pages)) return old;

            const queryFilters = (query?.queryKey && query.queryKey[1]) as Record<string, any> | undefined;
            const queryCrmStatusId = queryFilters?.crm_status_id;

            let totalChange = 0;
            const newPages = old.pages.map((page: any) => {
              if (!page || !Array.isArray(page.orders)) return page;
              const isOrderInPage = page.orders.some((o: any) => Number(o?.id) === Number(orderId));

              if (isOrderInPage) {
                if (
                  queryCrmStatusId !== undefined &&
                  queryCrmStatusId !== targetStatusId &&
                  queryCrmStatusId === oldCrmStatusId
                ) {
                  totalChange--;
                  return {
                    ...page,
                    orders: page.orders.filter((o: any) => Number(o?.id) !== Number(orderId)),
                  };
                }
                return {
                  ...page,
                  orders: page.orders.map((o: any) => (Number(o?.id) === Number(orderId) ? updatedOrder : o)),
                };
              }

              if (!isOrderInPage && queryCrmStatusId === targetStatusId) {
                const isFirstPage = old.pages[0] === page;
                if (isFirstPage) {
                  totalChange++;
                  return {
                    ...page,
                    orders: [updatedOrder, ...page.orders],
                  };
                }
              }

              return page;
            });

            // Si el total cambió, actualizarlo en la primera página (useCRM lo lee de ahí)
            if (totalChange !== 0 && newPages.length > 0) {
              newPages[0] = {
                ...newPages[0],
                total: Math.max(0, (newPages[0].total || 0) + totalChange),
              };
            }

            return Object.assign({}, old, { pages: newPages });
          });
        }

        // 3. Actualizar contadores
        queryClient.setQueriesData({ queryKey: ['crm-orders-count-by-status'] }, (oldCounts: any) => {
          if (!oldCounts) return oldCounts;
          const newCounts = Object.assign({}, oldCounts) as Record<string, number>;
          if (oldCrmStatusId !== null && newCounts[oldCrmStatusId] > 0) {
            newCounts[oldCrmStatusId]--;
          }
          newCounts[targetStatusId] = (newCounts[targetStatusId] || 0) + 1;
          return newCounts;
        });

        return { previousOrdersQueries, previousCountsQueries };
      } catch {
        return { previousOrdersQueries: [], previousCountsQueries: [] };
      }
    },
    onError: (_err, _, context) => {
      if (context?.previousOrdersQueries) {
        context.previousOrdersQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
      if (context?.previousCountsQueries) {
        context.previousCountsQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
    onSettled: () => {
      // Opcional: invalidar si queremos asegurar sincronización final,
      // pero el usuario pidió explícitamente SIN refetch.
      // queryClient.invalidateQueries({ queryKey: ['crm-orders', filters] });
    },
  });
};

export const useCancelOrder = (statusList?: CrmStatusModel[]) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: number) => apiAdapter.cancelOrdersBulk([orderId]),
    onMutate: async (orderId: number) => {
      try {
        await queryClient.cancelQueries({ queryKey: ['crm-orders'] });
        await queryClient.cancelQueries({ queryKey: ['crm-orders-count-by-status'] });

        const previousOrdersQueries = queryClient.getQueriesData({ queryKey: ['crm-orders'] });
        const previousCountsQueries = queryClient.getQueriesData({ queryKey: ['crm-orders-count-by-status'] });

        // Buscar el nuevo estado prioritariamente por nombre (flexible)
        const cancelledStatus =
          statusList && Array.isArray(statusList)
            ? statusList.find((s) => {
                const name = (s?.name || '').toLowerCase().trim();
                return name === 'cancelado' || name === 'cancelled' || name.includes('cancelad');
              })
            : undefined;

        const fallbackStatus =
          statusList && Array.isArray(statusList)
            ? statusList.find((s) => s?.id === ORDER_STATUS_ID.CANCELLED)
            : undefined;
        const targetStatus = cancelledStatus || fallbackStatus || { id: ORDER_STATUS_ID.CANCELLED, name: 'Cancelado' };
        const targetStatusId = targetStatus.id;

        const newStatusMetadata = {
          ...(targetStatus as object),
          managed_at: new Date().toISOString(),
          managed_by: { id: 0, name: 'Yo', email: '' },
        };

        // 1. Encontrar el pedido y su estado anterior
        let orderToMove: CRMModel | null = null;
        let oldCrmStatusId: number | null = null;

        previousOrdersQueries.forEach(([, data]: any) => {
          if (!orderToMove && data?.pages) {
            data.pages.forEach((page: any) => {
              if (!page || !Array.isArray(page.orders)) return;
              const found = page.orders.find((o: any) => Number(o?.id) === Number(orderId));
              if (found) {
                orderToMove = { ...(found as object) } as CRMModel;
                const status = Array.isArray(found.crm_status) ? found.crm_status[0] : found.crm_status;
                oldCrmStatusId = status?.id ?? null;
              }
            });
          }
        });

        if (orderToMove) {
          // Preparar el pedido actualizado
          const updatedOrder = {
            ...(orderToMove as object),
            crm_status: [newStatusMetadata],
            crm_status_id: targetStatusId,
            status_id: ORDER_STATUS_ID.CANCELLED,
          };

          // 2. Actualizar TODAS las queries de pedidos
          queryClient.setQueriesData({ queryKey: ['crm-orders'] }, (old: any, query: any) => {
            if (!old || !Array.isArray(old.pages)) return old;

            const queryFilters = (query?.queryKey && query.queryKey[1]) as Record<string, any> | undefined;
            const queryCrmStatusId = queryFilters?.crm_status_id;

            let totalChange = 0;
            const newPages = old.pages.map((page: any) => {
              if (!page || !Array.isArray(page.orders)) return page;
              const isOrderInPage = page.orders.some((o: any) => Number(o?.id) === Number(orderId));

              if (isOrderInPage) {
                if (
                  queryCrmStatusId !== undefined &&
                  queryCrmStatusId !== targetStatusId &&
                  queryCrmStatusId === oldCrmStatusId
                ) {
                  totalChange--;
                  return {
                    ...page,
                    orders: page.orders.filter((o: any) => Number(o?.id) !== Number(orderId)),
                  };
                }
                return {
                  ...page,
                  orders: page.orders.map((o: any) => (Number(o?.id) === Number(orderId) ? updatedOrder : o)),
                };
              }

              if (!isOrderInPage && queryCrmStatusId === targetStatusId) {
                const isFirstPage = old.pages[0] === page;
                if (isFirstPage) {
                  totalChange++;
                  return {
                    ...page,
                    orders: [updatedOrder, ...page.orders],
                  };
                }
              }

              return page;
            });

            // Si el total cambió, actualizarlo en la primera página (useCRM lo lee de ahí)
            if (totalChange !== 0 && newPages.length > 0) {
              newPages[0] = {
                ...newPages[0],
                total: Math.max(0, (newPages[0].total || 0) + totalChange),
              };
            }

            return Object.assign({}, old, { pages: newPages });
          });
        }

        // 3. Actualizar contadores
        queryClient.setQueriesData({ queryKey: ['crm-orders-count-by-status'] }, (oldCounts: any) => {
          if (!oldCounts) return oldCounts;
          const newCounts = Object.assign({}, oldCounts) as Record<string, number>;
          if (oldCrmStatusId !== null && newCounts[oldCrmStatusId] > 0) {
            newCounts[oldCrmStatusId]--;
          }
          newCounts[targetStatusId] = (newCounts[targetStatusId] || 0) + 1;
          return newCounts;
        });

        return { previousOrdersQueries, previousCountsQueries };
      } catch {
        return { previousOrdersQueries: [], previousCountsQueries: [] };
      }
    },
    onError: (_err, _, context) => {
      if (context?.previousOrdersQueries) {
        context.previousOrdersQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
      if (context?.previousCountsQueries) {
        context.previousCountsQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
  });
};

export const useUncancelOrder = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);

  return useMutation({
    mutationFn: (orderId: number) => apiAdapter.uncancelOrdersBulk([orderId]),
  });
};
